import { useState, useRef } from "react";
import axios from "axios";
import { Mic, Square } from "lucide-react";
import KipaIcon from "@/components/kipa/KipaIcon";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Voice recorder button. Tap to start/stop recording.
 * Sends audio to /api/voice/transcribe, then calls onTranscript(text).
 */
export default function VoiceButton({ onTranscript, disabled }) {
  const [recording, setRecording] = useState(false);
  const [processing, setProcessing] = useState(false);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);

  const start = async () => {
    if (disabled || recording || processing) return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const rec = new MediaRecorder(stream);
      chunksRef.current = [];
      rec.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };
      rec.onstop = async () => {
        try {
          const blob = new Blob(chunksRef.current, { type: "audio/webm" });
          streamRef.current.getTracks().forEach((t) => t.stop());
          if (blob.size < 1000) {
            setProcessing(false);
            return;
          }
          setProcessing(true);
          const fd = new FormData();
          fd.append("audio", blob, "recording.webm");
          const r = await axios.post(`${API}/voice/transcribe`, fd);
          if (r.data?.text) onTranscript(r.data.text);
        } catch (err) {
          console.error("Transcription failed", err);
          alert("Voice transcription failed. Please try again.");
        } finally {
          setProcessing(false);
        }
      };
      rec.start();
      recorderRef.current = rec;
      setRecording(true);
    } catch (err) {
      console.error(err);
      alert("Microphone access denied. Please allow mic in your browser.");
    }
  };

  const stop = () => {
    if (recorderRef.current && recording) {
      recorderRef.current.stop();
      setRecording(false);
    }
  };

  const toggle = () => {
    if (recording) stop();
    else start();
  };

  const isActive = recording || processing;

  return (
    <button
      onClick={toggle}
      disabled={disabled || processing}
      data-testid="voice-btn"
      title={recording ? "Tap to stop" : processing ? "Transcribing…" : "Tap to record"}
      className="w-11 h-11 rounded-full flex items-center justify-center flex-shrink-0 transition-all"
      style={{
        background: recording ? "var(--rd)" : processing ? "var(--go)" : "var(--sf2)",
        color: isActive ? "#fff" : "var(--tx2)",
        border: isActive ? "none" : "1px solid var(--br)",
        boxShadow: recording ? "0 0 0 4px rgba(184,50,50,0.18)" : "none",
      }}
    >
      {processing ? (
        <span className="typing-dot" style={{ background: "#fff" }} />
      ) : recording ? (
        <KipaIcon icon={Square} size="sm" color="#fff" />
      ) : (
        <KipaIcon icon={Mic} size="nav" color="var(--color-text-secondary)" />
      )}
    </button>
  );
}
